import React, { useState, useEffect } from "react";
import GoogleAdsAuth from "./GoogleAdsAuth";
import AccountSelector from "./AccountSelector";
import CampaignsTable from "./CampaignsTable";
import { TrendingUp, AlertCircle, Calendar } from "lucide-react";
import OverviewCards from "./OverViewCard";
import ApiService from "../services/api/api";
import { GoogleAdsAuthManager } from "../utils/auth";

const MainDashboard = () => {
  // Auth state
  const [isConnected, setIsConnected] = useState(false);
  // Data state
  const [accounts, setAccounts] = useState([]);
  const [selectedAccount, setSelectedAccount] = useState<any>(null);
  const [campaigns, setCampaigns] = useState([]);
  const [overview, setOverview] = useState(null);
  console.log("overview", overview);
  console.log("isConnected", isConnected);
  // UI state
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [dateRange, setDateRange] = useState("LAST_30_DAYS");

  // Check initial auth status
  useEffect(() => {
    // Initialize auth data if not present
    GoogleAdsAuthManager.initializeAuthData();
    checkAuthStatus();
  }, []);

  const checkAuthStatus = async () => {
    try {
      // First check if we have stored auth data
      const storedAuthData = GoogleAdsAuthManager.getAuthData();
      console.log(storedAuthData, "storedAuthData");
      if (storedAuthData && storedAuthData.isConnected) {
        setIsConnected(true);
        loadAccounts();
        return;
      }

      // If no stored auth data, check with server
      const status = await ApiService.getAuthStatus();
      setIsConnected(status.isConnected);
      console.log(status, "status");
      // Update stored auth data with server status
      GoogleAdsAuthManager.updateConnectionStatus(status.isConnected);

      if (status.isConnected) {
        loadAccounts();
      }
    } catch (error) {
      console.error("Failed to check auth status:", error);
      // Clear stored auth data on error
      GoogleAdsAuthManager.clearAuthData();
      setIsConnected(false);
    }
  };

  const handleAuthSuccess = () => {
    setIsConnected(true);
    // Update the auth manager status
    console.log("auth success");
    GoogleAdsAuthManager.updateConnectionStatus(true);
    loadAccounts();
  };

  const loadAccounts = async () => {
    try {
      setLoading(true);
      setError(null);
      const { accounts } = await ApiService.getAccounts();
      setAccounts(accounts);

      if (accounts.length > 0) {
        const defaultAccount = accounts[0];
        setSelectedAccount(defaultAccount);
        await loadAccountData(defaultAccount.id);
      }
      console.log(accounts, "accounts");
    } catch (error: any) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const loadAccountData = async (customerId: any) => {
    try {
      setLoading(true);
      setError(null);
      console.log(customerId, "customerId");
      // Load campaigns and overview in parallel
      const [campaignsResult, overviewResult] = await Promise.all([
        ApiService.getCampaigns(customerId, "default", dateRange),
        ApiService.getAccountOverview(customerId),
      ]);

      setCampaigns(campaignsResult.campaigns);
      setOverview(overviewResult.overview);
    } catch (error: any) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleAccountSelect = (account: any) => {
    setSelectedAccount(account);
    loadAccountData(account.id);
  };

  const handleDateRangeChange = (newRange: any) => {
    setDateRange(newRange);
    if (selectedAccount) {
      loadAccountData(selectedAccount.id);
    }
  };

  const handleRefresh = () => {
    if (selectedAccount) {
      loadAccountData(selectedAccount.id);
    }
  };

  // Show auth component if not connected
  if (!isConnected) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="max-w-4xl mx-auto">
          <GoogleAdsAuth onAuthSuccess={handleAuthSuccess} />
        </div>
      </div>
    );
  }

  return (
    <div className=" bg-gray-50">
      <div className="max-w-7xl mx-auto p-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 flex items-center">
              <TrendingUp className="w-8 h-8 mr-3 text-blue-600" />
              Google Ads Dashboard
            </h1>
            <p className="text-gray-600 mt-1">
              Monitor your advertising performance and insights
            </p>
          </div>

          <div className="flex items-center space-x-3 mt-4 sm:mt-0">
            {/* Date Range Selector */}
            <div className="flex items-center">
              <Calendar className="w-4 h-4 text-gray-400 mr-2" />
              <select
                value={dateRange}
                onChange={(e) => handleDateRangeChange(e.target.value)}
                className="border border-gray-300 rounded-md px-3 py-1 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="YESTERDAY">Yesterday</option>
                <option value="LAST_7_DAYS">Last 7 days</option>
                <option value="LAST_30_DAYS">Last 30 days</option>
                <option value="LAST_90_DAYS">Last 90 days</option>
                <option value="THIS_MONTH">This month</option>
                <option value="LAST_MONTH">Last month</option>
              </select>
            </div>
          </div>
        </div>

        {/* Auth Status */}
        <GoogleAdsAuth onAuthSuccess={handleAuthSuccess} />

        {/* Error Display */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
            <div className="flex items-center">
              <AlertCircle className="w-5 h-5 text-red-600 mr-2" />
              <p className="text-sm text-red-800">{error}</p>
            </div>
          </div>
        )}

        {/* Account Selector */}
        <AccountSelector
          accounts={accounts}
          selectedAccount={selectedAccount}
          onAccountSelect={handleAccountSelect}
          loading={loading && accounts.length === 0}
        />

        {/* Overview Cards */}
        <OverviewCards overview={overview} loading={loading} />

        {/* Campaigns Table */}
        <CampaignsTable
          campaigns={campaigns}
          loading={loading}
          onRefresh={handleRefresh}
        />
      </div>
    </div>
  );
};

export default MainDashboard;
